import moment from 'moment';
import { RouteLocation, RouteLocationRaw, Router } from 'vue-router';
import { ROUTE_DATE_FORMAT, ROUTE_DATE_TIME_FORMAT } from '@/planner/constants/general';
import { ensureString, isPresent } from '@/shared/utils/typescript';
import AuthService from '../services/AuthService';
import { HOME_ROUTE } from './constants';

export const EMPTY_PARAM_VALUE = '';

const INITIAL_ROUTE_KEY = 'route';

export type RouteParamValue = string | Array<string>;
export type RouteParams = Record<string, RouteParamValue>;

export function setInitialRoute(path: string): void {
  sessionStorage.setItem(INITIAL_ROUTE_KEY, path);
}

export function getInitialRoute(): RouteLocationRaw {
  const path = sessionStorage.getItem(INITIAL_ROUTE_KEY);
  if (path) {
    sessionStorage.removeItem(INITIAL_ROUTE_KEY);
    return { path };
  } else {
    return { name: HOME_ROUTE };
  }
}

export function formatParamValue(value: any, key?: string): RouteParamValue | undefined {
  if (value === undefined) return undefined;
  if (value === null) return EMPTY_PARAM_VALUE;
  if (value === true) return 'y';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_PARAM_VALUE;
    return value.map(x => formatParamValue(x, key)).filter(isPresent).map(v => ensureString(v));
  }
  if (moment.isMoment(value)) return value.format(key?.toLowerCase().endsWith('date') ? ROUTE_DATE_FORMAT : ROUTE_DATE_TIME_FORMAT);
}

export function buildRouteParams(source: Record<string, any>): RouteParams {
  return Object.entries(source).reduce((result, [key, value]) => {
    const formatted = formatParamValue(value, key);
    if (formatted !== undefined) result[key] = formatted;
    return result;
  }, {} as Record<string, RouteParamValue>);
}

export function routeIsPublic(route: RouteLocation): boolean {
  return route.meta.public === true;
}

export function routeIsAuthorized(route: RouteLocation): boolean {
  if (routeIsPublic(route)) return true;
  const actualPerms = AuthService.getCurrentUser().permissions;
  if (!actualPerms || actualPerms.length === 0) return false;
  return route.matched.map(record => record.meta.hasAccess).every(hasAccess => typeof hasAccess !== 'function' || hasAccess(actualPerms));
}

export enum RouteActivationStrategy {
  EXACT_PATH = 'exact-path',
  SUB_PATH = 'sub-path',
  FULL_PATH = 'full-path',
}

export function routeIsActive(route: RouteLocation, router: Router, strategy = RouteActivationStrategy.FULL_PATH): boolean {
  switch (strategy) {
    case RouteActivationStrategy.EXACT_PATH:
      return route.path === router.currentRoute.value.path;
    case RouteActivationStrategy.FULL_PATH:
      return route.fullPath === router.currentRoute.value.fullPath;
    case RouteActivationStrategy.SUB_PATH:
      return router.currentRoute.value.path.startsWith(route.path);
  }
}
